import {useCallback, useMemo, useState} from 'react';
import {z} from 'zod';

import {fetchAndDeserialize, postAndDeserialize} from '../../util/api-adapter';
import {catchError} from '../../util/promise';

export type UserConfigType = Record<string, unknown>;

export type UserConfigHookType = Readonly<{
    userConfig: UserConfigType;
    isInGettingUserConfig: boolean;
    isInSavingUserConfig: boolean;
    userConfigError: Error | null;
    getUserConfig: () => Promise<UserConfigType | Error>;
    setUserConfig: (config: UserConfigType) => Promise<UserConfigType | Error>;
    mergeUserConfig: (config: UserConfigType) => Promise<UserConfigType | Error>;
}>;

const userConfigUrl = '/cp/user/config/';

const userConfigResponseSchema = z.object({
    userId: z.number().nullable(),
    config: z.record(z.unknown()).nullable(),
});

type UserConfigResponseType = z.infer<typeof userConfigResponseSchema>;

async function fetchUserConfig(): Promise<UserConfigType> {
    const result = await fetchAndDeserialize<UserConfigResponseType>(userConfigUrl, userConfigResponseSchema);

    return result.config ?? {};
}

async function updateUserConfig(config: UserConfigType): Promise<UserConfigType> {
    const result = await postAndDeserialize<UserConfigResponseType>(userConfigUrl, userConfigResponseSchema, {config});

    return result.config ?? {};
}

export function useUserConfig(): UserConfigHookType {
    const [userConfigState, setUserConfigState] = useState<UserConfigType>({});
    const [isInGettingUserConfig, setIsInGettingUserConfig] = useState<boolean>(false);
    const [isInSavingUserConfig, setIsInSavingUserConfig] = useState<boolean>(false);
    const [userConfigError, setUserConfigError] = useState<Error | null>(null);

    const getUserConfig = useCallback(async (): Promise<UserConfigType | Error> => {
        setIsInGettingUserConfig(true);
        setUserConfigError(null);

        return fetchUserConfig()
            .then((result: UserConfigType): UserConfigType => {
                setUserConfigState(result);
                setIsInGettingUserConfig(false);

                return result;
            })
            .catch((error: unknown): Error => {
                const caughtError = catchError(error);

                setUserConfigError(caughtError);
                setIsInGettingUserConfig(false);

                return caughtError;
            });
    }, []);

    const setUserConfig = useCallback(async (config: UserConfigType): Promise<UserConfigType | Error> => {
        setIsInSavingUserConfig(true);
        setUserConfigError(null);

        return updateUserConfig(config)
            .then((result: UserConfigType): UserConfigType => {
                setUserConfigState(result);
                setIsInSavingUserConfig(false);

                return result;
            })
            .catch((error: unknown): Error => {
                const caughtError = catchError(error);

                setUserConfigError(caughtError);
                setIsInSavingUserConfig(false);

                return caughtError;
            });
    }, []);

    const mergeUserConfig = useCallback(
        async (config: UserConfigType): Promise<UserConfigType | Error> => {
            const nextUserConfig = {
                ...userConfigState,
                ...config,
            };

            setUserConfigState(nextUserConfig);

            return setUserConfig(nextUserConfig);
        },
        [setUserConfig, userConfigState]
    );

    return useMemo(
        () => ({
            userConfig: userConfigState,
            isInGettingUserConfig,
            isInSavingUserConfig,
            userConfigError,
            getUserConfig,
            setUserConfig,
            mergeUserConfig,
        }),
        [
            userConfigState,
            isInGettingUserConfig,
            isInSavingUserConfig,
            userConfigError,
            getUserConfig,
            setUserConfig,
            mergeUserConfig,
        ]
    );
}
